import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AdminLayout } from "@/components/shared/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { getAuditLog, getAuditLogFilters } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Filter, RefreshCcw, Download, Loader2, ShieldCheck, Search,
} from "lucide-react";

const ACTION_BADGE_COLOR = {
  create: "bg-emerald-100 text-emerald-700",
  update: "bg-blue-100 text-blue-700",
  delete: "bg-red-100 text-red-700",
  login: "bg-indigo-100 text-indigo-700",
  register: "bg-indigo-100 text-indigo-700",
  status_change: "bg-amber-100 text-amber-700",
  convert_to_dealer: "bg-purple-100 text-purple-700",
  convert_to_applicant: "bg-purple-100 text-purple-700",
  password_reset: "bg-orange-100 text-orange-700",
};

function formatDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString();
}

function toCSV(rows) {
  const headers = [
    "timestamp", "user_email", "user_name", "user_role",
    "action", "entity_type", "entity_id", "entity_name", "ip", "details",
  ];
  const esc = (v) => {
    if (v == null) return "";
    const s = typeof v === "object" ? JSON.stringify(v) : String(v);
    return `"${s.replace(/"/g, '""')}"`;
  };
  return [headers.join(","), ...rows.map((r) => headers.map((h) => esc(r[h])).join(","))].join("\n");
}

export default function AdminAuditLogReport() {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({
    start_date: "",
    end_date: "",
    user_id: "all",
    action: "all",
    entity_type: "all",
    search: "",
  });
  const [filterOptions, setFilterOptions] = useState({ actions: [], entity_types: [], users: [] });

  const fetchLog = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      if (filters.user_id && filters.user_id !== "all") params.user_id = filters.user_id;
      if (filters.action && filters.action !== "all") params.action = filters.action;
      if (filters.entity_type && filters.entity_type !== "all") params.entity_type = filters.entity_type;
      if (filters.search) params.search = filters.search;
      params.limit = 1000;
      const data = await getAuditLog(params);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load audit log");
    } finally {
      setLoading(false);
    }
  };

  const fetchFilters = async () => {
    try {
      const data = await getAuditLogFilters();
      setFilterOptions(data);
    } catch (e) {
      // silent
    }
  };

  useEffect(() => {
    fetchFilters();
    fetchLog();
    // eslint-disable-next-line
  }, []);

  const resetFilters = () => {
    setFilters({
      start_date: "", end_date: "",
      user_id: "all", action: "all", entity_type: "all", search: "",
    });
    setTimeout(fetchLog, 0);
  };

  const downloadCSV = () => {
    const csv = toCSV(items);
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const today = new Date().toISOString().slice(0, 10);
    a.download = `audit_log_${today}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const distinctRoles = useMemo(
    () => Array.from(new Set(items.map((i) => i.user_role).filter(Boolean))),
    [items]
  );

  return (
    <AdminLayout title="Audit Log Report" breadcrumb="Reports › Audit Log">
      <div data-testid="admin-audit-log-report">
        <div className="mb-4 flex items-center justify-between flex-wrap gap-2">
          <Link to="/admin/reports" className="inline-flex items-center text-sm text-teal hover:underline">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Reports
          </Link>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={resetFilters} data-testid="audit-reset-btn">
              <RefreshCcw className="w-4 h-4 mr-1" /> Reset
            </Button>
            <Button variant="outline" size="sm" onClick={downloadCSV} disabled={!items.length} data-testid="audit-csv-btn">
              <Download className="w-4 h-4 mr-1" /> Export CSV
            </Button>
          </div>
        </div>

        <Card className="mb-4">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Filter className="w-4 h-4 text-teal" /> Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Start Date</Label>
                <Input
                  type="date"
                  value={filters.start_date}
                  onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                  data-testid="audit-start-date"
                />
              </div>
              <div>
                <Label className="text-xs">End Date</Label>
                <Input
                  type="date"
                  value={filters.end_date}
                  onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                  data-testid="audit-end-date"
                />
              </div>
              <div>
                <Label className="text-xs">User</Label>
                <Select
                  value={filters.user_id}
                  onValueChange={(v) => setFilters({ ...filters, user_id: v })}
                >
                  <SelectTrigger data-testid="audit-user-filter"><SelectValue placeholder="All users" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All users</SelectItem>
                    {filterOptions.users.map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.name || u.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Action</Label>
                <Select
                  value={filters.action}
                  onValueChange={(v) => setFilters({ ...filters, action: v })}
                >
                  <SelectTrigger data-testid="audit-action-filter"><SelectValue placeholder="All actions" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All actions</SelectItem>
                    {filterOptions.actions.map((a) => (
                      <SelectItem key={a} value={a}>{a}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Entity Type</Label>
                <Select
                  value={filters.entity_type}
                  onValueChange={(v) => setFilters({ ...filters, entity_type: v })}
                >
                  <SelectTrigger data-testid="audit-entity-filter"><SelectValue placeholder="All types" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All types</SelectItem>
                    {filterOptions.entity_types.map((e) => (
                      <SelectItem key={e} value={e}>{e}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Search</Label>
                <div className="relative">
                  <Search className="w-4 h-4 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
                  <Input
                    className="pl-8"
                    placeholder="Search by name, email, entity..."
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    onKeyDown={(e) => e.key === "Enter" && fetchLog()}
                    data-testid="audit-search-input"
                  />
                </div>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between flex-wrap gap-2">
              <p className="text-xs text-slate-500">
                Showing <b>{items.length}</b> of <b>{total}</b> matching entries.
              </p>
              <Button onClick={fetchLog} disabled={loading} data-testid="audit-apply-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Filter className="w-4 h-4 mr-1" />}
                Apply Filters
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-teal" /> Audit Entries
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-6 h-6 animate-spin text-teal" />
              </div>
            ) : items.length === 0 ? (
              <div className="py-10 text-center text-slate-500">
                No audit entries match your filters.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm" data-testid="audit-log-table">
                  <thead>
                    <tr className="border-b border-slate-200 text-left text-slate-600">
                      <th className="py-2 pr-3 font-medium">When</th>
                      <th className="py-2 pr-3 font-medium">User</th>
                      <th className="py-2 pr-3 font-medium">Action</th>
                      <th className="py-2 pr-3 font-medium">Entity</th>
                      <th className="py-2 pr-3 font-medium">Target</th>
                      <th className="py-2 pr-3 font-medium">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it) => {
                      const badge = ACTION_BADGE_COLOR[it.action] || "bg-slate-100 text-slate-700";
                      return (
                        <tr key={it.id} className="border-b border-slate-100 hover:bg-slate-50/60">
                          <td className="py-2 pr-3 whitespace-nowrap text-slate-700">{formatDateTime(it.timestamp)}</td>
                          <td className="py-2 pr-3">
                            <div className="font-medium text-slate-800">{it.user_name || "—"}</div>
                            <div className="text-xs text-slate-500">{it.user_email}</div>
                          </td>
                          <td className="py-2 pr-3">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${badge}`}>
                              {it.action}
                            </span>
                          </td>
                          <td className="py-2 pr-3 text-slate-700">{it.entity_type}</td>
                          <td className="py-2 pr-3 text-slate-700 max-w-[260px] truncate" title={it.entity_name}>
                            {it.entity_name || "—"}
                          </td>
                          <td className="py-2 pr-3 text-xs text-slate-600 max-w-[320px] truncate" title={JSON.stringify(it.details || {})}>
                            {it.details && Object.keys(it.details).length > 0
                              ? Object.entries(it.details).map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : v}`).join(" · ")
                              : "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {distinctRoles.length > 0 && (
                  <p className="text-xs text-slate-400 mt-3">
                    Roles in view: {distinctRoles.join(", ")}
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
